// src/pages/Shop.js
import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, app } from '../services/firebase';
import SEO from '../components/SEO';
import './Shop.css';
import { useTranslation } from 'react-i18next';
import { pickL } from '../utils/i18nContent';

const DEFAULT_CATEGORY_LABELS = {
  tshirts: 'T-shirts',
  sweats: 'Sweats',
  pantalons: 'Pantalons',
  shorts: 'Shorts',
  vestes: 'Vestes',
  accessoires: 'Accessoires',
  goodies: 'Goodies'
};

function getCategoryLabel(product, lang = 'fr') {
  return (
    product.categoryLabel_i18n?.[lang] ||
    product.categoryLabel_i18n?.fr ||
    product.categoryLabel ||
    DEFAULT_CATEGORY_LABELS[product.category] ||
    product.category ||
    ''
  );
}

function Shop() {
  const { t, i18n } = useTranslation('shop');
  const lang = (i18n.language || 'fr').slice(0, 2);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderForm, setOrderForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    deliveryAddress: '',
    size: '',
    color: '',
    note: '',
    acceptedDelay: false
  });
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);

      try {
        const productsQuery = query(collection(db, 'products'), where('status', '==', 'published'));
        const snapshot = await getDocs(productsQuery);

        const items = snapshot.docs.map((document) => {
          const data = document.data();
          return {
            id: document.id,
            ...data,
            title: data.title || data.title_i18n?.fr || '',
            description: data.description || data.description_i18n?.fr || '',
            price: Number(data.price) || 0,
            comparePrice: Number(data.comparePrice) || 0,
            inventory: Number(data.inventory) || 0,
            status: data.status || 'draft',
            createdAt: data.createdAt?.toDate?.() || new Date(0)
          };
        });

        items.sort((a, b) => b.createdAt - a.createdAt);
        setProducts(items);
      } catch (error) {
        console.error('Erreur récupération produits boutique :', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const categories = useMemo(() => {
    const uniqueCategories = new Map();
    uniqueCategories.set('all', { id: 'all', label: t('filters.all') || 'Tous les produits' });

    products.forEach((product) => {
      if (!product.category) return;
      uniqueCategories.set(product.category, {
        id: product.category,
        label: getCategoryLabel(product, lang)
      });
    });

    return Array.from(uniqueCategories.values());
  }, [products, lang, t]);

  const filteredProducts = products.filter((product) => filter === 'all' || product.category === filter);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name': {
        const titleA = pickL(a.title_i18n || a.title, i18n.language, 'fr') || '';
        const titleB = pickL(b.title_i18n || b.title, i18n.language, 'fr') || '';
        return titleA.localeCompare(titleB, i18n.language);
      }
      case 'newest':
      default:
        return b.createdAt - a.createdAt;
    }
  });

  const openOrderForm = (product) => {
    setSelectedProduct(product);
    setOrderSuccess(false);
    setOrderError('');
    setOrderForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      deliveryAddress: '',
      size: product.sizes?.[0] || '',
      color: product.colors?.[0] || '',
      note: '',
      acceptedDelay: false
    });
  };

  const closeOrderForm = () => {
    if (isSubmittingOrder) return;
    setSelectedProduct(null);
    setOrderSuccess(false);
    setOrderError('');
  };

  const handleOrderChange = (event) => {
    const { name, value, type, checked } = event.target;
    setOrderForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const submitOrder = async (event) => {
    event.preventDefault();
    if (!selectedProduct) return;

    setIsSubmittingOrder(true);
    setOrderError('');

    try {
      const productTitle = pickL(selectedProduct.title_i18n || selectedProduct.title, i18n.language, 'fr');
      const categoryLabel = getCategoryLabel(selectedProduct, lang);

      const orderData = {
        status: 'pending',
        locale: lang,
        customer: {
          firstName: orderForm.firstName.trim(),
          lastName: orderForm.lastName.trim(),
          email: orderForm.email.trim(),
          phone: orderForm.phone.trim(),
          deliveryAddress: orderForm.deliveryAddress.trim()
        },
        product: {
          productId: selectedProduct.id,
          title: productTitle,
          category: selectedProduct.category || '',
          categoryLabel,
          price: Number(selectedProduct.price) || 0,
          image: selectedProduct.images?.[0]?.url || ''
        },
        selection: {
          size: orderForm.size,
          color: orderForm.color
        },
        note: orderForm.note.trim(),
        acceptedDelay: Boolean(orderForm.acceptedDelay),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const orderRef = await addDoc(collection(db, 'product_orders'), orderData);

      const functions = getFunctions(app, 'europe-west1');
      const createPayment = httpsCallable(functions, 'createPayment');
      const response = await createPayment({
        type: 'order',
        refId: orderRef.id,
        amount: Number(selectedProduct.price) || 0,
        description: `Boutique MKD Studio - ${productTitle}`
      });

      const checkoutUrl = response.data?.checkoutUrl;
      if (!checkoutUrl) {
        setOrderError(t('order.paymentLinkError'));
        setIsSubmittingOrder(false);
        return;
      }

      // Redirection complète vers le checkout Mollie (pas de popup)
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Erreur création commande produit:', error);
      setOrderError(t('order.genericError'));
      setIsSubmittingOrder(false);
    }
  };

  if (loading) {
    return (
      <div className="shop-container">
        <div className="container">
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>{t('loading') || 'Chargement...'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-container">
      <SEO title={t('seo.title')} description={t('seo.description')} keywords={t('seo.keywords')} />

      <div className="container">
        <div className="shop-header">
          <h1>{t('title')}</h1>
          <p className="shop-subtitle">{t('subtitle')}</p>

          <div className="payment-info-banner">
            <div className="payment-info-content">
              <span className="payment-icon">💰</span>
              <div>
                <strong>{t('payment.infoTitle')}</strong>
                <p>{t('payment.infoText')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="shop-controls">
          <div className="filters">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`filter-btn ${filter === category.id ? 'active' : ''}`}
                onClick={() => setFilter(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>

          <select className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="newest">{t('sort.newest') || 'Nouveautés'}</option>
            <option value="name">{t('sort.name') || 'Nom'}</option>
            <option value="price-low">{t('sort.priceLow') || 'Prix croissant'}</option>
            <option value="price-high">{t('sort.priceHigh') || 'Prix décroissant'}</option>
          </select>
        </div>

        {sortedProducts.length === 0 ? (
          <div className="no-products">
            <div className="empty-state">
              <span className="empty-icon">🛍️</span>
              <h3>{t('empty.title') || 'Aucun produit disponible'}</h3>
              <p>{t('empty.message') || 'Revenez bientôt pour découvrir notre nouvelle collection !'}</p>
            </div>
          </div>
        ) : (
          <div className="products-grid">
            {sortedProducts.map((product) => {
              const title = pickL(product.title_i18n || product.title, i18n.language, 'fr');
              const description = pickL(product.description_i18n || product.description, i18n.language, 'fr');
              const mainImage = product.images?.[0]?.url || '/placeholder-product.jpg';
              const hasDiscount = product.comparePrice > product.price;
              const discountPercent = hasDiscount ? Math.round((1 - product.price / product.comparePrice) * 100) : 0;

              return (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    <img src={mainImage} alt={title} onError={(e) => { e.currentTarget.src = '/placeholder-product.jpg'; }} />
                    {hasDiscount && <span className="discount-badge">-{discountPercent}%</span>}
                    {product.featured && <span className="featured-badge">{t('featured') || 'Vedette'}</span>}
                  </div>

                  <div className="product-info">
                    <h3 className="product-title">{title}</h3>
                    <p className="product-description">{description}</p>

                    <div className="product-price">
                      <span className="current-price">{product.price.toFixed(2)}€</span>
                      {hasDiscount && <span className="compare-price">{product.comparePrice.toFixed(2)}€</span>}
                    </div>

                    <div className="product-meta">
                      {product.category && <span className="sizes">{getCategoryLabel(product, lang)}</span>}
                      {product.sizes?.length > 0 && <span className="sizes">{t('sizes') || 'Tailles'} : {product.sizes.join(', ')}</span>}
                      <div className="stock-info"><span className="in-stock">Fabriqué à la commande</span></div>
                    </div>

                    <div className="product-actions">
                      <button className="buy-button" onClick={() => openOrderForm(product)}>
                        {t('buyButton') || 'Commander'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="payment-instructions">
          <h3>{t('payment.howToTitle') || 'Comment commander ?'}</h3>
          <div className="steps">
            <div className="step"><div className="step-number">1</div><h4>{t('payment.step1')}</h4><p>{t('payment.step1Desc')}</p></div>
            <div className="step"><div className="step-number">2</div><h4>{t('payment.step2')}</h4><p>{t('payment.step2Desc')}</p></div>
            <div className="step"><div className="step-number">3</div><h4>{t('payment.step3')}</h4><p>{t('payment.step3Desc')}</p></div>
          </div>
        </div>
      </div>

      {selectedProduct && (
        <div className="order-modal-backdrop" onClick={closeOrderForm}>
          <div className="order-modal" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="order-modal-close" onClick={closeOrderForm}>×</button>

            {orderSuccess ? (
              <div className="order-success">
                <h2>{t('order.successTitle')}</h2>
                <p>{t('order.successMessage1')}</p>
                <p>{t('order.successMessage2')}</p>
                <button className="buy-button" onClick={closeOrderForm}>{t('order.close')}</button>
              </div>
            ) : (
              <form className="order-form" onSubmit={submitOrder}>
                <h2>{t('order.title')}</h2>
                <p><strong>{pickL(selectedProduct.title_i18n || selectedProduct.title, i18n.language, 'fr')}</strong> — {Number(selectedProduct.price || 0).toFixed(2)}€</p>

                {orderError && <div className="order-error">{orderError}</div>}

                <div className="order-form-grid">
                  <label>{t('order.firstName')} *<input name="firstName" value={orderForm.firstName} onChange={handleOrderChange} required /></label>
                  <label>{t('order.lastName')} *<input name="lastName" value={orderForm.lastName} onChange={handleOrderChange} required /></label>
                  <label>{t('order.email')} *<input type="email" name="email" value={orderForm.email} onChange={handleOrderChange} required /></label>
                  <label>{t('order.phone')}<input name="phone" value={orderForm.phone} onChange={handleOrderChange} /></label>

                  {selectedProduct.sizes?.length > 0 && (
                    <label>{t('order.size')}
                      <select name="size" value={orderForm.size} onChange={handleOrderChange}>
                        {selectedProduct.sizes.map((size) => <option key={size} value={size}>{size}</option>)}
                      </select>
                    </label>
                  )}

                  {selectedProduct.colors?.length > 0 && (
                    <label>{t('order.color')}
                      <select name="color" value={orderForm.color} onChange={handleOrderChange}>
                        {selectedProduct.colors.map((color) => <option key={color} value={color}>{color}</option>)}
                      </select>
                    </label>
                  )}
                </div>

                <label>{t('order.deliveryAddress')}<textarea name="deliveryAddress" value={orderForm.deliveryAddress} onChange={handleOrderChange} rows="3" placeholder={t('order.deliveryAddressPlaceholder')} /></label>
                <label>{t('order.note')}<textarea name="note" value={orderForm.note} onChange={handleOrderChange} rows="3" /></label>

                <label className="order-checkbox">
                  <input type="checkbox" name="acceptedDelay" checked={orderForm.acceptedDelay} onChange={handleOrderChange} required />
                  <span>{t('order.delayConsent')}</span>
                </label>

                <button type="submit" className="buy-button" disabled={isSubmittingOrder}>
                  {isSubmittingOrder ? t('order.submitting') : t('order.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Shop;